// License: PROPRIETARY

// Approving adds the offending version to a policy's approved range as a new
// disjunction arm. The grammar the server accepts is documented in
// sources/dashboard/js/policies/library-version-range.js: arms are separated by
// "|", and an arm is either a bare version or an interval. Nothing here parses
// user input - every value comes from the server - so there is no validation to
// duplicate.
export const addVersionToRange = (range, version) => {
    const existingArms = String(range || "")
        .split("|")
        .map((arm) => arm.trim())
        .filter((arm) => arm.length > 0)

    if (existingArms.includes(version)) {
        return { range: existingArms.join("|"), added: false }
    }
    return { range: [...existingArms, version].join("|"), added: true }
}

export const APPROVAL_KINDS = {
    CREATE: "create",
    EXISTING: "existing"
}

// The dashboard's own create call sends exactly {name, language}
// (sources/dashboard/js/policies/library-admin.js:63). advice.library carries
// extra fields the committed fixtures show (e.g. rootLanguage), and sending
// them verbatim risks a 400 whose message is shown to the user unfiltered. The
// shape is decided here, where it is tested, rather than at the call site.
const libraryFor = (advice) => (
    advice.library ? { name: advice.library.name, language: advice.library.language } : null
)

// The body for a create request: no policy exists yet, so nothing is echoed
// from the advice except what was actually observed - the library, the
// offending version standing alone as the new range, and the severity, which
// on a no-policy violation is the account's severityForUnapprovedLibrary
// default (per the API guide). Exported so the component can also reach it
// when a get() 404s on the "policy exists" path below - see ApproveLibrary.js.
export const createRequestFor = (advice, version) => ({
    library: libraryFor(advice),
    versionRange: version,
    severity: advice.severity
})

// sourceID links the advice to the policy governing this library: absent
// (null, undefined or empty) means no policy exists yet, so approving
// creates one; present means one already does, so approving updates that
// policy. This was confirmed by the product owner and matches the live
// payload - all 407 policy-violation advices observed, from an account with
// the feature enabled and zero library policies, carried sourceID: null.
//
// The advice's own id is NOT the policy uuid, despite the API guide's claim
// that it is - the owner states sourceID is what carries the policy's
// identifier, and that is what is used below.
//
// The two sub-states the API guide names for "a policy exists" - inactive,
// or active but out of range - cannot be told apart from the advice alone:
// their distinguishing messages have not been observed, and there is no
// other field left to read (the payload carries no `active` at all).
// Resolving that split, if it is ever wanted, needs a real with-policy
// payload; until then both collapse into one kind whose only correct next
// step is to read the real policy from the server before deciding anything -
// which is why this plan deliberately carries no range, description or
// active flag of its own.
export const approvalPlanFor = (advice, version) => {
    const policyUuid = advice.sourceID

    if (!policyUuid) {
        return {
            kind: APPROVAL_KINDS.CREATE,
            ...createRequestFor(advice, version),
            description: "",
            policyUuid: null
        }
    }

    // Deliberately thin: range, severity and description belong to the real
    // policy, not to this advice, and must not be guessed here. The component
    // reads the policy at policyUuid before building an update.
    return {
        kind: APPROVAL_KINDS.EXISTING,
        policyUuid: policyUuid,
        version: version,
        severity: advice.severity
    }
}

// How a library-policy violation is told apart from every other advice.
//
// Resolved and verified 2026-08-04 against a live report: an account with the
// feature enabled and zero library policies returned 407 policy-violation
// advices, all carrying source: "policy", and no other advice in the same
// report used that value - including the 188 non-policy advices that also
// have cve: null, which is why a CVE-based test was never a safe substitute.
// The API guide's claim that a violation "appears ... with no new handling"
// describes how it flows through the UI, not that it is indistinguishable -
// it always is, once you know the field.
//
// This marker is safety-critical, not merely tidy: versionRange means the
// offending version on a policy violation but the VULNERABLE range on an
// ordinary advisory, so a wrong marker would read a vulnerable range as
// something to approve, and widen it - approving exactly the versions that
// are unsafe, silently and with a successful response.
export const LIBRARY_POLICY_ADVICE_MARKER = { field: "source", value: "policy" }

export const matchesMarker = (advice, marker) => {
    if (!advice || !marker || marker.value === null) return false
    return advice[marker.field] === marker.value
}

export const isLibraryPolicyViolation = (advice) => matchesMarker(advice, LIBRARY_POLICY_ADVICE_MARKER)

// Finds the policy governing an advice's library, by matching the library
// rather than by following a uuid.
//
// This exists because sourceID is null on EVERY policy-violation advice,
// including the ones where a policy demonstrably does exist: an axios advice
// reading "Version 0.19.1 is outside the approved range [0.0.1,0.0.2]" and
// carrying the policy's own CRITICAL severity still arrives with
// sourceID: null. approvalPlanFor() therefore reports CREATE for a library
// that already has a policy, and approving would open the create modal and
// try to create a second one - when the operation is an update of the range
// on the policy already there.
//
// Matched on name + language, the same pair the dashboard's own
// findByLibrary() uses, and case-insensitively on the name for the same
// reason it does. The policy list comes from all(scopeUuid), so the scope is
// already the one the caller asked about.
export const findPolicyForAdvice = (policies, advice) => {
    const library = (advice && advice.library) || {}
    const name = (library.name || "").toLowerCase()
    if (!name) return null
    return (policies || []).filter((policy) =>
        (policy.name || "").toLowerCase() === name &&
        policy.language === library.language
    )[0] || null
}

// The body for updating an EXISTING plan's policy, once the caller has read
// it from the server (see ApproveLibrary.js). update() is a full replace, so
// every value here comes from the real policy, never from the advice: the
// advice's "description" is the server's prose message, not the policy's
// note, and the policy's shape when one really exists is still unobserved -
// see the follow-ups doc.
//
// active is always true, unconditionally: approving an inactive policy
// re-enables it, so there is no case where this piece would send false.
//
// isUnchanged is true only when the version is already an arm of the range
// AND the policy was already active - the one case where sending the update
// at all would be a no-op request rather than a real change.
export const updateBodyFor = (policy, version) => {
    const widened = addVersionToRange(policy.versionRange, version)
    return {
        versionRange: widened.range,
        severity: policy.severity,
        description: policy.description || "",
        active: true,
        isUnchanged: !widened.added && policy.active === true
    }
}

// What the user is told when an approval request fails. It lives here, beside
// the request builders, because two callers now send those requests - the
// Approve button for the update path and the create modal for the create path
// - and one wording for one failure is the point.
//
// The server's own message wins when there is one: it is more specific than
// anything that could be guessed from the status alone.
export const messageForFailure = (failure) => {
    if (failure && failure.serverMessage) return failure.serverMessage
    const status = failure ? failure.status : null
    if (status === 401) return "You are not allowed to approve libraries for this team."
    if (status === 409) return "A policy for this library was created in the meantime."
    if (status === 404) return "Library policies are not available for this account."
    return "The approval could not be sent. Please try again."
}

const APPROVALS_STORAGE_KEY = "meterian.libraryPolicyApprovals"

// An approval is a statement about ONE report: the version was approved while
// looking at this one. That is what makes the report uuid the key and gives the
// record a reset - a recompute produces a new report, so nothing carries over,
// and a policy later narrowed in the dashboard can only surface in a new report
// too. Entries belonging to any other report are dropped on write.
//
// This is a UI hint about one rendered report, never a claim about current
// policy state. The server remains the source of truth; the worst a wrong entry
// can do is show "Approved" until the next recompute.
//
// The unit of approval is (reportUuid, adviceId, version), not (reportUuid,
// adviceId). On a library-policy violation advice.id is the POLICY uuid, which
// is identical across every component that policy governs - a project on
// commons-collections4 4.0 and 4.5 both violating policy P renders two rows
// that both carry id === P. Keying on adviceId alone would make approving 4.0
// also mark 4.5 as approved, though no request was ever sent for 4.5, and would
// permanently hide the escape hatch the grouped view's single-component
// collapse depends on (see the design doc's "known limitations": "the other
// versions are approvable from the component-list view"). The composite key
// below keeps each version's approval independent.
const approvalKey = (adviceId, version) => `${adviceId}@${version}`

const NOTHING_APPROVED = { reportUuid: null, approvedKeys: [] }

const readApprovals = () => {
    try {
        const stored = window.sessionStorage.getItem(APPROVALS_STORAGE_KEY)
        if (!stored) return { ...NOTHING_APPROVED }
        const parsed = JSON.parse(stored)
        if (!parsed || !Array.isArray(parsed.approvedKeys)) return { ...NOTHING_APPROVED }
        return parsed
    }
    catch (storageUnavailable) {
        return { ...NOTHING_APPROVED }
    }
}

const writeApprovals = (approvals) => {
    try { window.sessionStorage.setItem(APPROVALS_STORAGE_KEY, JSON.stringify(approvals)) }
    catch (storageUnavailable) { /* a lost hint is not worth breaking the report over */ }
}

export const markApproved = (reportUuid, adviceId, version) => {
    if (!reportUuid || !adviceId || !version) return
    const key = approvalKey(adviceId, version)
    const approvals = readApprovals()
    const isSameReport = approvals.reportUuid === reportUuid
    const approvedKeys = isSameReport ? approvals.approvedKeys : []
    if (approvedKeys.includes(key)) return
    writeApprovals({ reportUuid: reportUuid, approvedKeys: [...approvedKeys, key] })
}

export const wasApproved = (reportUuid, adviceId, version) => {
    if (!reportUuid || !adviceId || !version) return false
    const approvals = readApprovals()
    if (approvals.reportUuid !== reportUuid) return false
    return approvals.approvedKeys.includes(approvalKey(adviceId, version))
}

// Exists so tests do not leak state into one another.
export const resetApprovals = () => {
    try { window.sessionStorage.removeItem(APPROVALS_STORAGE_KEY) }
    catch (storageUnavailable) { /* nothing to clear */ }
}
